import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api, { describeApiError, isCancelled } from "@/lib/api";
import useAbortSignal from "@/lib/useAbortSignal";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Folders, Plus, MagnifyingGlass, Archive, ArrowCounterClockwise, Trash, Prohibit, Copy
} from "@phosphor-icons/react";
import { AccessDenied } from "@/components/AccessDenied";

const ragColors = {
  green: { bg: "bg-[#F0FDF4]", border: "border-l-[#16A34A]", text: "text-[#16A34A]", label: "On Track" },
  amber: { bg: "bg-[#FFFBEB]", border: "border-l-[#D97706]", text: "text-[#D97706]", label: "At Risk" },
  red: { bg: "bg-[#FEF2F2]", border: "border-l-[#DC2626]", text: "text-[#DC2626]", label: "Off Track" },
};

export default function ProjectsPage() {
  const { user, canSee, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const nextSignal = useAbortSignal();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("active");
  const [typeFilter, setTypeFilter] = useState("client");  // "client" | "lead" | "internal"
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [projectSort, setProjectSort] = useState("name");
  const [form, setForm] = useState({ project_type: "client", name: "", description: "", tenant_id: "", lead_id: "", start_date: "", end_date: "", budget: 0, master_start_date: "", must_complete_by: "", pm_name: "", pm_email: "" });

  // Permission gates for the 3 project types
  const isAdmin = hasRole("platform_admin");
  const canSeeLeadProjects = isAdmin || canSee?.("leads");
  const canSeeInternalProjects = isAdmin || canSee?.("support");
  const canSeeClientProjects = isAdmin || canSee?.("projects") || user?.user_type === "client";

  useEffect(() => { loadData(); }, []);

  // Deep-link support: /projects?type=lead sets the tab on mount
  useEffect(() => {
    const qt = new URLSearchParams(location.search).get("type");
    if (qt && ["client", "lead", "internal"].includes(qt)) {
      setTypeFilter(qt);
      setForm(f => ({ ...f, project_type: qt }));
    }
  }, [location.search]);

  if (user?.user_type === "internal" && !canSee("projects") && !canSeeLeadProjects && !canSeeInternalProjects) return <AccessDenied module="Projects" />;

  const loadData = async () => {
    // Cancels the previous load (and is aborted on unmount), so leaving
    // Projects never leaves its requests running. The spinner ends once
    // the PROJECTS list settles; the lead picker loads in the background.
    const signal = nextSignal();
    setLoadError(null);
    // Fetch projects and tenants independently. /api/tenants returns 403 for
    // users without the `clients` module (e.g. developers); we don't want
    // that to wipe out the projects list — we just lose the client-name
    // labels (the project payload already includes tenant_name as a join).
    try {
      const projRes = await api.get("/projects", { signal });
      setProjects(projRes.data);
    } catch (err) {
      if (isCancelled(err)) return;
      console.error("Failed to load projects:", err);
      setLoadError(describeApiError(err));
    }
    if (!signal.aborted) setLoading(false);
    try {
      const tenantRes = await api.get("/tenants", { signal });
      setTenants(tenantRes.data);
    } catch { /* user lacks `clients` module — fine, project rows already carry tenant_name */ }
    if (canSeeLeadProjects) {
      // Lead picker only needs ids/names — `view=picker` skips the heavy
      // marketing-state enrichment that froze this page (2026-10-21).
      try { const r = await api.get("/crm/leads?view=picker", { signal }); setLeads(r.data || []); } catch { /* leads not available */ }
    }
  };

  const handleCreate = async () => {
    if (!form.name) return toast.error("Project name is required");
    if (form.project_type === "client" && !form.tenant_id) return toast.error("Client is required for Client projects");
    if (form.project_type === "lead" && !form.lead_id) return toast.error("Lead is required for Lead projects");
    try {
      const payload = { ...form };
      // Clean irrelevant fields
      if (payload.project_type !== "client") payload.tenant_id = null;
      if (payload.project_type !== "lead") payload.lead_id = null;
      await api.post("/projects", payload);
      toast.success(`${form.project_type === "client" ? "Client" : form.project_type === "lead" ? "Lead" : "Internal"} project created`);
      setShowCreate(false);
      setForm({ project_type: typeFilter, name: "", description: "", tenant_id: "", lead_id: "", start_date: "", end_date: "", budget: 0, master_start_date: "", must_complete_by: "", pm_name: "", pm_email: "" });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create project"); }
  };

  const handleArchive = async (e, id) => { e.stopPropagation(); try { await api.put(`/projects/${id}/archive`); toast.success("Project archived"); loadData(); } catch (err) { toast.error("Failed"); } };
  const handleUnarchive = async (e, id) => { e.stopPropagation(); try { await api.put(`/projects/${id}/unarchive`); toast.success("Restored"); loadData(); } catch (err) { toast.error("Failed"); } };
  const handleDelete = async () => { if (!confirmDelete) return; try { await api.delete(`/projects/${confirmDelete}`); toast.success("Deleted"); setConfirmDelete(null); loadData(); } catch (err) { toast.error("Failed"); } };
  const handlePause = async (e, id) => { e.stopPropagation(); try { await api.put(`/projects/${id}`, { status: "paused" }); toast.success("Paused"); loadData(); } catch (err) { toast.error("Failed"); } };
  const handleResume = async (e, id) => { e.stopPropagation(); try { await api.put(`/projects/${id}`, { status: "active" }); toast.success("Resumed"); loadData(); } catch (err) { toast.error("Failed"); } };
  const handleCopy = async (e, id) => {
    e.stopPropagation();
    try {
      const r = await api.post(`/projects/${id}/copy`);
      toast.success(`Copied — ${r.data.tasks_copied} task${r.data.tasks_copied === 1 ? "" : "s"}, ${r.data.team_copied} team member${r.data.team_copied === 1 ? "" : "s"}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Copy failed"); }
  };

  const filtered = projects.filter(p => {
    const ptype = p.project_type || "client";
    if (ptype !== typeFilter) return false;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || (p.owner_label || p.tenant_name || "").toLowerCase().includes(search.toLowerCase());
    if (viewMode === "active") return matchSearch && p.status !== "archived";
    if (viewMode === "archived") return matchSearch && p.status === "archived";
    return matchSearch;
  }).sort((a, b) => {
    if (projectSort === "client") return (a.owner_label || a.tenant_name || "").localeCompare(b.owner_label || b.tenant_name || "");
    if (projectSort === "progress") { const pa = a.task_count > 0 ? a.done_count / a.task_count : 0; const pb = b.task_count > 0 ? b.done_count / b.task_count : 0; return pb - pa; }
    if (projectSort === "status") return (a.rag_status || "").localeCompare(b.rag_status || "");
    return (a.name || "").localeCompare(b.name || "");
  });

  const countByType = {
    client: projects.filter(p => (p.project_type || "client") === "client").length,
    lead: projects.filter(p => p.project_type === "lead").length,
    internal: projects.filter(p => p.project_type === "internal").length,
  };
  const archivedCount = projects.filter(p => (p.project_type || "client") === typeFilter && p.status === "archived").length;
  const activeCount = projects.filter(p => (p.project_type || "client") === typeFilter && p.status !== "archived").length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  return (
    <div className="page-enter p-6 sm:p-8 space-y-6" data-testid="projects-page">
      {loadError && (
        <div className="border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="projects-load-error">
          <p className="text-sm text-[#0A0A0A]"><b>Projects could not be loaded.</b> {loadError}</p>
          <button onClick={() => { setLoading(true); loadData(); }}
            className="h-8 px-3 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC]">Retry</button>
        </div>
      )}
      <div className="flex items-center justify-start gap-6 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Projects</h1>
          <p className="text-sm text-[#525252] mt-1">Manage project delivery</p>
        </div>
        {user?.user_type === "internal" && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none px-6 h-10" data-testid="create-project-button"><Plus size={16} weight="bold" className="mr-2" />New Project</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
              <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Create Project</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Project Type</Label>
                  <div className="flex gap-2">
                    {[
                      { val: "client", label: "Client", visible: canSeeClientProjects, color: "#0055FF" },
                      { val: "lead", label: "Lead", visible: canSeeLeadProjects, color: "#7C3AED" },
                      { val: "internal", label: "Internal", visible: canSeeInternalProjects, color: "#0A0A0A" },
                    ].filter(t => t.visible).map(t => (
                      <button
                        key={t.val}
                        type="button"
                        onClick={() => setForm({ ...form, project_type: t.val, tenant_id: "", lead_id: "" })}
                        className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${form.project_type === t.val ? "text-white border-transparent" : "text-[#525252] border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
                        style={form.project_type === t.val ? { backgroundColor: t.color } : {}}
                        data-testid={`project-type-${t.val}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Project Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" placeholder="e.g. Website Redesign" data-testid="project-name-input" /></div>
                {form.project_type === "client" && (
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Client</Label><Select value={form.tenant_id} onValueChange={v => setForm({...form, tenant_id: v})}><SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="project-tenant-select"><SelectValue placeholder="Select client" /></SelectTrigger><SelectContent className="rounded-none">{tenants.filter(t => t.status !== "archived" && t.status !== "inactive").map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
                )}
                {form.project_type === "lead" && (
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Sales Lead</Label><Select value={form.lead_id} onValueChange={v => setForm({...form, lead_id: v})}><SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="project-lead-select"><SelectValue placeholder="Select lead" /></SelectTrigger><SelectContent className="rounded-none">{leads.map(l => { const id = l.lead_id || l.id; const person = l.contact_person || l.contact_name; return <SelectItem key={id} value={id}>{l.company_name || person} {person ? `- ${person}` : ""}</SelectItem>; })}</SelectContent></Select></div>
                )}
                {form.project_type === "internal" && (
                  <div className="text-xs text-[#A3A3A3] italic">Internal Eyvo project — not tied to a client or lead.</div>
                )}
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-none border-[#E5E5E5] min-h-[80px]" data-testid="project-description-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" /></div>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#DC2626]">Master Start Date</Label><Input type="date" value={form.master_start_date} onChange={e => setForm({...form, master_start_date: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" /></div>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#DC2626]">Must Complete By</Label><Input type="date" value={form.must_complete_by} onChange={e => setForm({...form, must_complete_by: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">PM Name</Label><Input value={form.pm_name} onChange={e => setForm({...form, pm_name: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" /></div>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">PM Email</Label><Input type="email" value={form.pm_email} onChange={e => setForm({...form, pm_email: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" /></div>
                </div>
                <Button onClick={handleCreate} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="submit-project-button">Create Project</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex border border-[#E5E5E5]">
          {canSeeClientProjects && (
            <button onClick={() => setTypeFilter("client")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${typeFilter === "client" ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-type-client">
              Client Projects ({countByType.client})
            </button>
          )}
          {canSeeLeadProjects && (
            <button onClick={() => setTypeFilter("lead")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${typeFilter === "lead" ? "bg-[#7C3AED] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-type-lead">
              Lead Projects ({countByType.lead})
            </button>
          )}
          {canSeeInternalProjects && (
            <button onClick={() => setTypeFilter("internal")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${typeFilter === "internal" ? "bg-[#0A0A0A] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-type-internal">
              Internal Projects ({countByType.internal})
            </button>
          )}
        </div>
        <div className="flex border border-[#E5E5E5]">
          <button onClick={() => setViewMode("active")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${viewMode === "active" ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-active-projects">Active ({activeCount})</button>
          <button onClick={() => setViewMode("archived")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${viewMode === "archived" ? "bg-[#525252] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-archived-projects">Archived ({archivedCount})</button>
        </div>
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <Input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} className="rounded-none border-[#E5E5E5] h-10 pl-9" data-testid="search-projects-input" />
        </div>
        <select value={projectSort} onChange={e => setProjectSort(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-xs font-bold uppercase tracking-wider focus:outline-none focus:border-[#0055FF] h-10" data-testid="projects-sort">
          <option value="name">Sort: Name A-Z</option>
          <option value="client">Sort: Client</option>
          <option value="progress">Sort: Progress</option>
          <option value="status">Sort: RAG Status</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(project => {
          const rag = ragColors[project.rag_status] || ragColors.green;
          const progress = project.task_count > 0 ? Math.round((project.done_count / project.task_count) * 100) : 0;
          const isArchived = project.status === "archived";
          const isPaused = project.status === "paused";
          return (
            <Card key={project.id} className={`border rounded-none p-5 cursor-pointer hover:shadow-sm transition-all border-l-4 ${isPaused ? "bg-[#FFF5F5] border-[#FECACA] border-l-[#F87171]" : isArchived ? "border-[#E5E5E5] border-l-[#A3A3A3] opacity-75" : `border-[#E5E5E5] ${rag.border}`}`}
              onClick={() => navigate(`/projects/${project.id}`)} data-testid={`project-card-${project.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {project.project_type === "lead" && <Badge className="bg-[#F5F0FF] text-[#7C3AED] border-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-none">Lead</Badge>}
                    {project.project_type === "internal" && <Badge className="bg-[#F5F5F5] text-[#0A0A0A] border-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-none">Internal</Badge>}
                    <h3 className="font-bold text-[#0A0A0A] truncate">{project.owner_label || project.tenant_name}</h3>
                  </div>
                  <p className="text-xs text-[#525252] mt-0.5">{project.project_number && <span className="text-[#0055FF] font-bold mr-1.5">{project.project_number}</span>}{project.name}</p>
                </div>
                {isPaused ? <Badge className="bg-[#FEE2E2] text-[#DC2626] border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none ml-2 flex-shrink-0">Paused</Badge>
                  : isArchived ? <Badge className="bg-[#F5F5F5] text-[#A3A3A3] border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none ml-2 flex-shrink-0">Archived</Badge>
                  : <Badge className={`${rag.bg} ${rag.text} border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none ml-2 flex-shrink-0`}>{rag.label}</Badge>}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-[#525252]"><span>{project.done_count}/{project.task_count} tasks</span><span className="font-bold">{progress}%</span></div>
                <div className="w-full h-1.5 bg-[#F5F5F5]"><div className="h-full bg-[#0055FF] transition-all" style={{ width: `${progress}%` }} /></div>
                {project.master_start_date && project.must_complete_by && (() => {
                  const start = new Date(project.master_start_date);
                  const end = new Date(project.must_complete_by);
                  const now = new Date();
                  const total = end - start;
                  const elapsed = Math.max(0, Math.min(now - start, total));
                  const pct = total > 0 ? Math.round((elapsed / total) * 100) : 0;
                  return (
                    <div>
                      <div className="flex items-center justify-between text-[10px] text-[#A3A3A3] mb-1"><span>{project.master_start_date}</span><span>{project.must_complete_by}</span></div>
                      <div className="w-full h-1.5 bg-[#F5F5F5] relative"><div className="h-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #16A34A, #7C3AED)" }} /></div>
                    </div>
                  );
                })()}
              </div>
              {user?.user_type === "internal" && (() => {
                const perms = project.user_permissions || {};
                const canPause = perms.is_pm || perms.is_admin;
                const canArchive = perms.is_admin || perms.is_pm;
                return (
                  <div className="mt-3 pt-3 border-t border-[#F5F5F5] flex gap-2 flex-wrap">
                    {isArchived ? canArchive && <>
                      <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#E5E5E5]" onClick={e => handleUnarchive(e, project.id)}><ArrowCounterClockwise size={12} className="mr-1" />Restore</Button>
                      <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]" onClick={e => { e.stopPropagation(); setConfirmDelete(project.id); }}><Trash size={12} className="mr-1" />Delete</Button>
                    </> : isPaused ? <>
                      {canPause && <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]" onClick={e => handleResume(e, project.id)}><ArrowCounterClockwise size={12} className="mr-1" />Resume</Button>}
                      {canArchive && <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#E5E5E5]" onClick={e => handleArchive(e, project.id)}><Archive size={12} className="mr-1" />Archive</Button>}
                    </> : <>
                      {canPause && <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#F87171] text-[#DC2626] hover:bg-[#FFF5F5]" onClick={e => handlePause(e, project.id)}><Prohibit size={12} className="mr-1" />Pause</Button>}
                      {canArchive && <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#E5E5E5]" onClick={e => handleCopy(e, project.id)} data-testid={`copy-project-${project.id}`}><Copy size={12} className="mr-1" />Copy</Button>}
                      {canArchive && <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#E5E5E5]" onClick={e => handleArchive(e, project.id)}><Archive size={12} className="mr-1" />Archive</Button>}
                    </>}
                  </div>
                );
              })()}
            </Card>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16"><Folders size={48} className="mx-auto text-[#E5E5E5] mb-4" /><p className="text-[#A3A3A3] font-medium">{viewMode === "archived" ? "No archived projects" : "No projects found"}</p></div>
      )}

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-sm">
          <DialogHeader><DialogTitle className="font-bold text-[#DC2626]">Delete Project Permanently?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252]">This will permanently delete the project and all its data. This action cannot be undone.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-none h-10" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold" onClick={handleDelete} data-testid="confirm-delete-project">Delete Forever</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
